import { StatusPill } from '../ui/StatusPill';

/**
 * Who holds authority over an asset, in what capacity, and why — rendered from
 * the backend `authority` sub-object (Phase 2, BRD EV-01 / EV-02).
 *
 * Renders only what the API returned. When `authority` is absent the card falls
 * back to the flat `current_holder` / `authority_state` / `custody_state` fields
 * so older payloads still show a holder, with no version or reason.
 *
 * @param {{
 *   authority?: {
 *     version: number,
 *     holder: string,
 *     holder_org_id?: string|null,
 *     capacity?: string|null,
 *     authority_state: string,
 *     custody_state: string,
 *     reason?: string|null,
 *     source_type?: string|null,
 *     recorded_at?: string|null,
 *   } | null,
 *   fallback?: { current_holder?: string, authority_state?: string, custody_state?: string } | null,
 *   holderOrgName?: string|null,
 *   className?: string,
 *   title?: string,
 * }} props
 */
export function AuthorityStateCard({
  authority,
  fallback = null,
  holderOrgName = null,
  className = '',
  title = 'Authority record',
}) {
  const holder = authority?.holder ?? fallback?.current_holder ?? null;
  const authorityState = authority?.authority_state ?? fallback?.authority_state ?? null;
  const custodyState = authority?.custody_state ?? fallback?.custody_state ?? null;
  if (!holder && !authorityState && !custodyState) return null;

  return (
    <section
      aria-label={title}
      className={`rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card ${className}`}
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-sm font-semibold text-eorig-ink">{title}</h2>
        {authority ? (
          <span className="font-mono text-[11px] text-eorig-muted" data-testid="authority-version">
            v{authority.version}
          </span>
        ) : null}
      </div>
      <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">Holder</dt>
          <dd className="mt-0.5 flex flex-wrap items-center gap-2">
            {holder ? <StatusPill value={holder} label={holderLabel(holder)} /> : '—'}
            {holderOrgName ? <span className="text-eorig-ink">{holderOrgName}</span> : null}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">Capacity</dt>
          <dd className="mt-0.5 text-eorig-ink">{authority?.capacity ? humanize(authority.capacity) : '—'}</dd>
        </div>
        <div>
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">Authority</dt>
          <dd className="mt-0.5">{authorityState ? <StatusPill value={authorityState} /> : '—'}</dd>
        </div>
        <div>
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">Custody</dt>
          <dd className="mt-0.5">{custodyState ? <StatusPill value={custodyState} /> : '—'}</dd>
        </div>
        <div>
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">Reason</dt>
          <dd className="mt-0.5 text-eorig-ink">{authority?.reason ? humanize(authority.reason) : '—'}</dd>
        </div>
        <div>
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">Recorded</dt>
          <dd className="mt-0.5 text-eorig-ink">{formatDateTime(authority?.recorded_at)}</dd>
        </div>
      </dl>
      {!authority ? (
        <p className="mt-3 text-[11px] text-eorig-muted">
          Authority history is not available for this record yet.
        </p>
      ) : null}
    </section>
  );
}

export function holderLabel(holder) {
  switch (holder) {
    case 'ORIGINATOR':
      return 'Originator';
    case 'CUSTODIAN':
      return 'Custodian';
    case 'PROVIDER':
      return 'Provider';
    case 'SPV':
      return 'SPV';
    default:
      return holder || '—';
  }
}

function humanize(value) {
  if (!value) return '—';
  return String(value)
    .toLowerCase()
    .split('_')
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ');
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
